import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { AssessmentDto } from "@green0meter/api-client";

import type { QuestionItemType } from "@api";
import {
  Avatar,
  Chip,
  Stack,
  Typography,
  Box,
  Tooltip,
} from "@components/base";
import Table, { TableColumn } from "@components/common/Table";
import { getAvatarPropsByName } from "@helpers";

type StringSet = Set<string>;

interface QuestionsTableProps {
  questions: QuestionItemType[];
  allStakeholdersCount: number;
  selectedQuestions: StringSet;
  setSelectedQuestions: (s: StringSet) => void;
  questionAssignments: Record<string, string[]>;
  assessments: AssessmentDto[];
}

const QuestionsTable: React.FC<QuestionsTableProps> = ({
  questions,
  allStakeholdersCount,
  selectedQuestions,
  setSelectedQuestions,
  questionAssignments,
  assessments,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase();
  const currentLanguageBase = currentLanguage.split("-")[0];

  const getLocalizedTranslation = (value: unknown): string => {
    if (typeof value === "string") {
      return value;
    }

    if (!value || typeof value !== "object") {
      return "";
    }

    const translations = Object.entries(value as Record<string, unknown>).reduce(
      (acc, [key, translation]) => {
        if (typeof translation === "string" && translation.trim()) {
          acc[key.toLowerCase()] = translation;
        }

        return acc;
      },
      {} as Record<string, string>,
    );
    const fallbackOrder = [currentLanguage, currentLanguageBase, "en", "cs"];

    for (const languageKey of fallbackOrder) {
      const translation = translations[languageKey];

      if (translation) {
        return translation;
      }
    }

    return Object.values(translations)[0] ?? "";
  };

  const columns: TableColumn<QuestionItemType>[] = [
    {
      title: t("assessments.table.assessmentId", "Assessment ID"),
      key: "assessmentId",
      renderCellContent: (row) => {
        const assessment = assessments.find((a) => a.id === row.assessmentId);

        return (
          <Typography variant="body2">
            {assessment?.assessmentId ?? row.assessmentId ?? "-"}
          </Typography>
        );
      },
      tableCellProps: { sx: { width: 160 } },
    },
    {
      title: t("doubleMateriality.questions.table.question", "Question"),
      key: "questionText",
      renderCellContent: (row) => {
        const value = getLocalizedTranslation(row.questions);

        return value ? (
          <Typography
            component="div"
            variant="body2"
            sx={{
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxHeight: "100%",
              maxWidth: 560,
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("doubleMateriality.questions.noTranslation", {
              defaultValue: `No translation for {{lng}}`,
              lng: currentLanguageBase,
            })}
          </Typography>
        );
      },
      tableCellProps: { sx: { width: 560 } },
    },
    {
      title: t("doubleMateriality.assignedTo.header", "Assigned to"),
      key: "assignStatus",
      renderCellContent: (row) => {
        const assignedNames = row.id ? questionAssignments[row.id] ?? [] : [];
        const assignedToAll =
          assignedNames.length > 0 && assignedNames.length === allStakeholdersCount;

        if (assignedToAll) {
          return (
            <Chip
              label={t("assignTo.allStakeholders", "All stakeholders")}
              color="success"
              size="small"
            />
          );
        }

        if (assignedNames.length === 0) {
          return (
            <Chip
              label={t("doubleMateriality.notVisible", "Not visible")}
              color="error"
              size="small"
              variant="filled"
            />
          );
        }

        const maxVisible = 8;
        const displayedNames = assignedNames.slice(0, maxVisible);
        const remainingCount = assignedNames.length - displayedNames.length;

        return (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap", py: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {displayedNames.map((name, idx) => {
                const avatarProps = getAvatarPropsByName(name);

                return (
                  <Tooltip key={`${name}-${idx}`} title={name} arrow>
                    <Avatar
                      {...avatarProps}
                      sx={{
                        ...avatarProps.sx,
                        width: 32,
                        height: 32,
                        fontSize: 12,
                        border: "2px solid",
                        borderColor: theme.palette.background.paper,
                        boxSizing: "border-box",
                        ml: idx === 0 ? 0 : -1.25,
                        zIndex: displayedNames.length - idx,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount} ${t(
                  "doubleMateriality.assignedTo.otherStakeholders",
                  "Other stakeholders"
                )}`}
                color="success"
                size="small"
                sx={{
                  height: 32,
                  borderRadius: "999px",
                  fontWeight: 600,
                  px: 1.5,
                  "& .MuiChip-label": { px: 0 },
                }}
              />
            )}
          </Stack>
        );
      },
      tableCellProps: { sx: { width: 400 } },
    },
  ];

  const selectedRowIds = useMemo(() => Array.from(selectedQuestions), [selectedQuestions]);

  return (
    <Box sx={{ marginLeft: "96px !important" }}>
      <Table
        columns={columns}
        data={questions}
        enableRowSelection
        selectedRowIds={selectedRowIds}
        onSelectionChange={(newSelection) => {
          setSelectedQuestions(new Set(newSelection));
        }}
        dataPlaceholder={
          <Typography color="text.secondary">
            {t(
              "doubleMateriality.questions.emptyOnNoFilter",
              "No questions. Use filters above."
            )}
          </Typography>
        }
        tableProps={{
          sx: {
            "& .MuiTableCell-root": {
              verticalAlign: "top",
            }
          }
        }}
      />
    </Box>
  );
};

export default QuestionsTable;
